'use client';

import { compactNumber } from '@inkitt/galatea-ui/utils/compactNumber';
import { generateStorySlug } from '@inkitt/utils-web';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingChapterSidebar } from '../../../components/FloatingChapterButton/FloatingChapterSidebar';
import { GalateaParagraphWeb as GalateaParagraph } from '../../../components/GalateaParagraphWeb';
import { HarlequinBadge } from '../../../components/HarlequinBadge';
import { TrackableLink } from '../../../components/TrackableLink';
import { useChapterEndTracking } from '../../../hooks/useChapterEndTracking';
import { useStoryTracking } from '../../../hooks/useStoryTracking';
import { trackAdjustEvent } from '../../../services/adjust/adjustService';
import { type StoryData, type EpisodesList, type EpisodeContent } from '../../../services/api/goApi';
import { type ReadingProgressItem } from '../../../services/api/progressApi';
import { isEpisodeAvailable } from '../../../services/pricing/dynamicPricingService';
import { getStoryEventProperties, trackStoryEvent } from '../../../services/tracking/trackClientInfo';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/selectors/select-user';
import type { CategoryPaths } from '../../../utils/categoryPaths';
import { cn } from '../../../utils/cn';
import { lockedChapterDestination } from '../../../utils/lockedChapterDestination';
import { getChapterPath } from '../../../utils/slugUtils';
import { matchTagsWithNRR, type NrrShelfWebIds } from '../../../utils/tagMatching';
import { FormattedSummary } from './FormattedSummary';
import { SummaryHighlight } from './SummaryHighlight';

type ParagraphData = EpisodeContent['content'][0]['data'][0];

const toGalateaParagraphItem = (paragraph: ParagraphData) => ({
  sound: undefined,
  styles: undefined,
  vibrate: undefined,
  povStyle: {},
  ...paragraph,
});

const SummaryHighlights: React.FC<{
  reads: number;
  chapters: number;
  author: string;
}> = ({ reads, chapters, author }) => {
  const { t } = useTranslation();

  return (
    <>
      {author.trim() && (
        <SummaryHighlight label={t('summary:highlight_author')} first>
          {author.trim()}
        </SummaryHighlight>
      )}
      <SummaryHighlight label={t('summary:highlight_reads')} first={!author.trim()}>
        <span className="flex items-center gap-1">
          {reads > 10_000_000 && (
            <span role="img" aria-label="fire">
              🔥
            </span>
          )}
          {compactNumber(reads)}
        </span>
      </SummaryHighlight>
      <SummaryHighlight label={t('summary:highlight_chapters')}>{chapters}</SummaryHighlight>
    </>
  );
};

const TabButton: React.FC<{
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ isActive, onClick, children }) => (
  <button
    onClick={onClick}
    role="tab"
    aria-selected={isActive}
    tabIndex={isActive ? 0 : -1}
    className={cn(
      'py-2 px-1 text-sm font-semibold transition-colors border-b-2 border-x-0 border-t-0 border-solid bg-transparent outline-hidden',
      isActive ? 'text-neutral-900 border-primary-300' : 'text-neutral-500 hover:text-neutral-700 border-transparent',
    )}
  >
    {children}
  </button>
);

const NextChapterButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <div className="pt-8 flex justify-center">
      <button
        onClick={onClick}
        aria-label={t('summary:next_chapter') || 'Next Chapter'}
        className={cn(
          'bg-primary-300 hover:bg-primary-200 active:bg-primary-200',
          'text-white font-extrabold py-3 px-8 md:px-32 rounded-md',
          'transition-colors focus:ring-2 focus:ring-primary-100 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'w-full md:w-auto text-center',
        )}
      >
        {t('summary:next_chapter')}
      </button>
    </div>
  );
};

const ChapterContent: React.FC<{
  firstChapterContent: EpisodeContent;
  forwardRef?: React.RefObject<HTMLDivElement | null>;
  keyPrefix?: string;
  contentLocale: string;
}> = ({ firstChapterContent, forwardRef, contentLocale, keyPrefix = '' }) => {
  return (
    <div lang={contentLocale} className="pt-6 border-t border-neutral-300 font-noto-serif">
      <h2 className="text-2xl font-bold text-neutral-900 text-center">{firstChapterContent.title}</h2>
      <div className="space-y-2 mt-4 select-none">
        {firstChapterContent.content.map((section, sectionIndex) =>
          section.data.map((paragraph: ParagraphData, paragraphIndex) => {
            const isLastSection = sectionIndex === firstChapterContent.content.length - 1;
            const isLastParagraph = paragraphIndex === section.data.length - 1;
            const isLastParagraphOfChapter = isLastSection && isLastParagraph;

            return (
              <div
                key={`${keyPrefix}${section.id}-${sectionIndex}-${
                  paragraph.block_id || paragraph.id || paragraphIndex
                }`}
                ref={isLastParagraphOfChapter ? forwardRef : undefined}
              >
                <GalateaParagraph
                  item={toGalateaParagraphItem(paragraph)}
                  index={paragraphIndex}
                  fontSize={16}
                  chatDeviceHolder={section.chatDeviceHolder}
                  isDarkMode={false}
                />
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
};

export const SummaryPage: React.FC<{
  storyData: StoryData;
  chaptersData: EpisodesList;
  firstChapterContent?: EpisodeContent;
  storyId: string;
  numericStoryId: string;
  locale: string;
  contentLocale?: string;
  /**
   * Recommendations band, rendered by the server as a streaming island (DEV-5531). Required rather
   * than optional: this component is the band's only render site, so a caller that leaves it out
   * drops the band with nothing to fail on.
   */
  similarBooks: ReactNode;
  readsCount: number;
  progress?: ReadingProgressItem | null;
  isSubscribed?: boolean;
  nrrShelfWebIds?: NrrShelfWebIds;
  categoryPaths?: CategoryPaths;
}> = ({
  storyData,
  chaptersData,
  firstChapterContent,
  storyId,
  numericStoryId,
  locale,
  contentLocale = locale,
  similarBooks,
  readsCount,
  progress,
  isSubscribed = false,
  nrrShelfWebIds = {},
  categoryPaths = {},
}) => {
  const { t } = useTranslation(['summary']);
  const [activeTab, setActiveTab] = useState<'summary' | 'chapters'>('summary');
  const router = useRouter();
  const pathname = usePathname();
  const { uid } = useAppSelector(selectUser);
  const lastParagraphRef = useRef<HTMLDivElement>(null);

  useStoryTracking(numericStoryId, pathname, 'summary', 1, storyData.publisher);

  useChapterEndTracking({
    storyId,
    storyName: storyData.name,
    originalStoryId: storyData.id,
    chapterNumber: 1,
    chapterTitle: firstChapterContent?.title || 'Chapter 1',
    isSummaryScreen: true,
    publisher: storyData.publisher,
    elementRef: lastParagraphRef,
  });

  const handleStartReading = () => {
    trackStoryEvent('summary-start-reading-clicked', storyData, {
      storyId: storyData.id,
      storyName: storyData.name,
      author: storyData.author,
    });

    const chapterPath = getChapterPath(storyData.name, parseInt(numericStoryId, 10), 2, locale, storyData.url_slug);
    router.push(`/${locale}${chapterPath}`);
  };

  const reads = readsCount;
  const chapters = chaptersData?.episodes?.length || 0;

  const sortedEpisodes = useMemo(() => {
    if (chaptersData?.episodes) {
      return chaptersData.episodes.sort((a, b) => a.number - b.number);
    }
    return [];
  }, [chaptersData]);

  const matchedTags = useMemo(() => {
    return matchTagsWithNRR(storyData.grouped_tags || [], nrrShelfWebIds, categoryPaths);
  }, [storyData.grouped_tags, nrrShelfWebIds, categoryPaths]);

  const handleChapterNavigation = (episode: { number: number; title: string }) => {
    const episodeNumber = episode.number;

    trackStoryEvent('summary_chapter-link_clicked', storyData, {
      storyName: storyData.name,
      originalStoryId: storyData.id,
      number: episode.number,
      chapterTitle: episode.title,
      slug: storyId,
      isAvailable: isEpisodeAvailable(
        episodeNumber,
        progress?.maxUnlockedEpisodeId || 1,
        chaptersData?.free_episodes_count || 0,
        chaptersData?.episodes,
      ),
      isSubscribed,
    });

    const storySlug = generateStorySlug(storyData.name, parseInt(numericStoryId, 10), locale, storyData.url_slug);

    if (
      isEpisodeAvailable(
        episodeNumber,
        progress?.maxUnlockedEpisodeId || 1,
        chaptersData?.free_episodes_count || 0,
        chaptersData?.episodes,
      )
    ) {
      router.push(`/${locale}/story/${storySlug}/chapter/${episodeNumber}`);
    } else if (isSubscribed) {
      router.push(`/${locale}/story/${storySlug}/chapter/${episodeNumber}`);
    } else {
      router.push(lockedChapterDestination({ locale, storySlug, chapterNumber: episodeNumber, userId: uid }));
    }
  };

  useEffect(() => {
    trackAdjustEvent('View_Content');
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <div className="container mx-auto px-4 pt-12">
        <main className="max-w-4xl mx-auto flex flex-col items-center" role="main">
          <header className="flex flex-row items-center gap-8 w-full">
            {/* Book Cover */}
            <div className="relative w-[120px] md:w-56 aspect-[194/300] rounded-[16px] overflow-hidden shadow-lg">
              <Image
                src={storyData.cover || ''}
                alt={`Cover image for ${storyData.name}`}
                fill
                priority
                sizes="(max-width: 768px) 120px, 224px"
                className="object-cover"
              />
              <HarlequinBadge publisher={storyData.publisher} />
            </div>

            {/* Title & Highlights */}
            <div className="flex-1 flex flex-col items-start justify-center">
              <h1 lang={contentLocale} className="text-3xl font-bold text-neutral-900 mb-2">
                {storyData.name}
              </h1>
              <div
                className="hidden md:flex flex-row flex-wrap items-center text-sm mt-4"
                role="region"
                aria-label="Story details"
              >
                <SummaryHighlights reads={reads} chapters={chapters} author={storyData.author} />
              </div>
            </div>
          </header>

          <div className="md:hidden mt-4 w-full max-w-4xl mx-auto">
            <div
              className="flex overflow-x-auto space-x-2 -mx-4 px-4 pb-2 no-scrollbar"
              role="region"
              aria-label="Story details"
            >
              <SummaryHighlights reads={reads} chapters={chapters} author={storyData.author} />
            </div>
          </div>
        </main>

        {/* Genre Tags */}
        {matchedTags.length > 0 && (
          <section className="mt-8 w-full max-w-4xl mx-auto" aria-label="Story genres">
            <div className="flex md:flex-wrap overflow-x-auto md:overflow-x-visible space-x-2 md:gap-2 md:space-x-0 -mx-4 md:mx-0 px-4 md:px-0 pb-2 md:pb-0 no-scrollbar">
              {matchedTags.map((genre) => {
                const pillClassName =
                  'shrink-0 md:flex-shrink flex items-center gap-1 bg-neutral-300 rounded-full px-3 py-2 font-semibold text-sm transition-colors no-underline';
                const pillContents = (
                  <>
                    {genre.custom_image && (
                      <img src={genre.custom_image} alt="" aria-hidden="true" className="w-4 h-4 object-contain" />
                    )}
                    {/* genre.title fallback contains the emoji already */}
                    {genre.localized_title ? (
                      <>
                        {!genre.custom_image && genre.emoji && (
                          <span role="img" aria-hidden="true">
                            {genre.emoji}
                          </span>
                        )}
                        {genre.localized_title}
                      </>
                    ) : (
                      genre.title
                    )}
                  </>
                );

                if (!genre.categoryPath) {
                  return (
                    <span
                      key={genre.slug}
                      className={pillClassName}
                      aria-label={`Category: ${genre.localized_title || genre.title}`}
                    >
                      {pillContents}
                    </span>
                  );
                }

                return (
                  <TrackableLink
                    key={genre.slug}
                    path={genre.categoryPath}
                    className={cn(pillClassName, 'hover:bg-neutral-400')}
                    trackingInfo={{
                      name: 'category_tag_clicked',
                      properties: getStoryEventProperties(storyData, {
                        category_id: genre.id,
                        category_slug: genre.slug,
                        category_title: genre.localized_title || genre.title,
                        source: 'summary_page',
                        nrr_web_id: genre.nrrWebId,
                      }),
                    }}
                    aria-label={genre.localized_title || genre.title}
                  >
                    {pillContents}
                  </TrackableLink>
                );
              })}
            </div>
          </section>
        )}

        {/* Tabs */}
        <div className="mt-8 max-w-4xl mx-auto">
          <div className="border-b border-neutral-300">
            <nav className="-mb-px flex space-x-4" role="tablist" aria-label="Story details navigation">
              <TabButton isActive={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
                {t('summary:summary')}
              </TabButton>
              <TabButton isActive={activeTab === 'chapters'} onClick={() => setActiveTab('chapters')}>
                {t('summary:chapters')}
              </TabButton>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'summary' && (
              <div role="tabpanel" aria-labelledby="summary-tab" tabIndex={0}>
                {storyData.summary && (
                  <div className="space-y-8">
                    <div lang={contentLocale} className="space-y-4">
                      <FormattedSummary content={storyData.summary.content} />
                    </div>
                    {firstChapterContent && (
                      <ChapterContent
                        firstChapterContent={firstChapterContent}
                        contentLocale={contentLocale}
                        forwardRef={lastParagraphRef}
                        keyPrefix="about-"
                      />
                    )}
                  </div>
                )}
                <NextChapterButton onClick={handleStartReading} />
              </div>
            )}

            {activeTab === 'chapters' && sortedEpisodes.length > 0 && (
              <div role="tabpanel" aria-labelledby="chapters-tab" tabIndex={1}>
                <div className="space-y-8">
                  <div lang={contentLocale} className="space-y-4 md:ml-6">
                    {sortedEpisodes.map((episode) => (
                      <button
                        key={episode.id}
                        onClick={() => handleChapterNavigation(episode)}
                        className="block text-left text-neutral-900 underline hover:text-primary-300 font-semibold bg-transparent border-none p-0 cursor-pointer"
                      >
                        {episode.title}
                      </button>
                    ))}
                  </div>
                  {firstChapterContent && (
                    <ChapterContent
                      firstChapterContent={firstChapterContent}
                      contentLocale={contentLocale}
                      forwardRef={lastParagraphRef}
                      keyPrefix="chapters-"
                    />
                  )}
                </div>
                <NextChapterButton onClick={handleStartReading} />
              </div>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto w-full mt-12">
          <div className="border-t border-neutral-300" />
          {similarBooks}
        </div>
      </div>

      {chaptersData?.episodes && (
        <FloatingChapterSidebar
          storyTitle={storyData.name}
          urlSlug={storyData.url_slug}
          storyAuthor={storyData.author}
          storyId={numericStoryId}
          episodes={chaptersData.episodes}
          locale={locale}
          progress={progress}
          freeEpisodesCount={chaptersData?.free_episodes_count || 0}
          isSubscribed={isSubscribed}
          publisher={storyData.publisher}
        />
      )}
    </div>
  );
};
